import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, ShieldCheck, Truck } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="anasayfa"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#fafafa] pt-24"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 pt-12 pb-16 lg:pt-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm w-fit"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00] animate-pulse" />
              <span className="font-mono-disp text-[11px] font-medium text-gray-700 tracking-wide">
                İnci Tel Çit Sistemleri — Her Şehre Hizmet
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display mt-7 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold tracking-tighter leading-[1] text-gray-900"
            >
              Dayanıklı tel,{" "}
              <span className="font-serif-disp text-[#FF5A00] font-normal">
                güvenilir
              </span>{" "}
              çit.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="font-body mt-6 max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed"
            >
              25 yılı aşkın deneyimimizle galvanizli tel, bakır tel, örgü çit ve
              dikenli tel üretiminde Türkiye&apos;nin güvenilir endüstriyel
              üreticisiyiz.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("urunler")}
                className="btn-mod btn-primary btn-shift"
              >
                Ürünlerimizi Keşfet
                <ArrowUpRight className="arrow w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("iletisim")}
                className="btn-mod btn-ghost-dark btn-shift"
              >
                Hemen Teklif Al
                <ArrowUpRight className="arrow w-4 h-4" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600"
            >
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#FF5A00]" />
                ISO 9001:2015 Sertifikalı
              </span>
              <span className="inline-flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#FF5A00]" />
                81 İlde Hızlı Sevkiyat
              </span>
            </motion.div>
          </div>

          {/* Right: image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full rounded-[2rem] overflow-hidden bg-gray-100 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)]">
              <img
                src="https://customer-assets.emergentagent.com/job_telecom-pro-6/artifacts/13tvo0d6_IMG-20260610-WA0006.jpg"
                alt="İnci Tel modern bahçe kapısı uygulaması"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

              {/* Floating stat cards */}
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                <div className="bg-white/90 backdrop-blur-md px-3.5 py-2 rounded-full font-mono-disp text-[10px] font-semibold text-gray-900">
                  EST. 1998
                </div>
                <div className="bg-[#FF5A00] text-white px-3.5 py-2 rounded-full font-mono-disp text-[10px] font-semibold">
                  Türkiye Üretimi
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl p-5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.25)] border border-gray-100 z-10">
              <div className="font-mono-disp text-[10px] text-gray-500 uppercase tracking-wide">
                Yıllık üretim kapasitesi
              </div>
              <div className="font-display text-3xl font-bold text-gray-900 mt-1 tracking-tight">
                120K{" "}
                <span className="text-base font-medium text-gray-500">
                  ton/yıl
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { k: "25+", v: "Yıllık Deneyim" },
            { k: "850+", v: "Tamamlanan Proje" },
            { k: "81", v: "İlde Hizmet Ağı" },
            { k: "%99.6", v: "Müşteri Memnuniyeti" },
          ].map((s) => (
            <div
              key={s.k}
              className="card-mod px-5 py-6 flex flex-col"
            >
              <div className="font-display text-4xl font-bold tracking-tight text-gray-900">
                {s.k}
              </div>
              <div className="font-body text-sm text-gray-500 mt-1.5">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
